import { useConfiguratorApi } from '@/composables/api/useConfiguratorApi.ts'
import type { Configurator, DispatchMetadata, McpServerPayload } from 'shared/src'
import { useExport } from '@/composables/exports/useExport'
import { useToast } from '@ai/ui-components/toast'
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'

export type ReducedDispatchMetadata = Omit<DispatchMetadata, 'settings'> & {
  settings: Pick<
    DispatchMetadata['settings'],
    | 'sampling'
    | 'flags'
    | 'speech'
    | 'inactivity'
    | 'features'
    | 'vad'
    | 'form_schema'
    | 'external_tools'
  >
}

const PROTECTED_MCP_KEYS = new Set(['label', 'url', 'type', 'headers', 'timeout_seconds', 'tools'])

export const useConfigurations = () => {
  const { t } = useI18n({ useScope: 'global' })
  const toast = useToast()
  const { exportToJson } = useExport()

  const { getConfigurationData } = useConfiguratorApi()

  const isExportingId = ref<string | null>(null)

  const buildVaExportFileName = (item: Configurator, data?: DispatchMetadata) => {
    const sanitizeFilePart = (value?: string | null) => {
      if (!value) return 'unknown'
      return (
        value
          .trim()
          .replace(/\s+/g, '_')
          .replace(/[^a-zA-Z0-9_-]/g, '_')
          .replace(/_+/g, '_') || 'unknown'
      )
    }

    const formatExportTimestamp = (date: Date) => {
      const pad = (n: number) => String(n).padStart(2, '0')
      return `${String(date.getFullYear())}${pad(date.getMonth() + 1)}${pad(date.getDate())}-${pad(
        date.getHours(),
      )}${pad(date.getMinutes())}`
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const agentName = data?.settings?.general_information?.agent_name ?? item.name ?? 'VA'
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const agentId = item.id ?? 'unknown'
    return `VA-${sanitizeFilePart(agentName)}-${sanitizeFilePart(agentId)}-${formatExportTimestamp(
      new Date(),
    )}.json`
  }

  const reduceConfigurationForExport = (config: DispatchMetadata): ReducedDispatchMetadata => {
    const { settings, scenarios, ...rest } = config
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, @typescript-eslint/prefer-optional-chain
    if (!settings || !settings.speech) {
      throw new Error('missing_configuration')
    }

    const reducedSettings: ReducedDispatchMetadata['settings'] = {
      speech: settings.speech,
      ...(settings.sampling ? { sampling: settings.sampling } : {}),
      ...(settings.flags ? { flags: settings.flags } : {}),
      ...(settings.inactivity ? { inactivity: settings.inactivity } : {}),
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      ...(settings.call_duration_limits
        ? { call_duration_limits: settings.call_duration_limits }
        : {}),
      ...(settings.knowledge ? { knowledge: settings.knowledge } : {}),
      ...(settings.features ? { features: settings.features } : {}),
      ...(settings.vad ? { vad: settings.vad } : {}),
      ...(settings.form_schema ? { form_schema: settings.form_schema } : {}),
      ...(settings.external_tools
        ? {
            external_tools: {
              ...settings.external_tools,
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              servers: (settings.external_tools.servers ?? []).map(
                ({ config_overrides, ...server }: Record<string, unknown>) =>
                  ({
                    ...server,
                    ...(config_overrides
                      ? Object.fromEntries(
                          Object.entries(config_overrides as Record<string, unknown>).filter(
                            ([k]) => !PROTECTED_MCP_KEYS.has(k),
                          ),
                        )
                      : {}),
                  }) as McpServerPayload,
              ),
            },
          }
        : {}),
    }

    const additionalParams = settings.additional_parameters as Record<string, unknown> | undefined
    const reducedScenarios = scenarios.map((scenario, i) => ({
      ...(i === 0 && additionalParams ? additionalParams : {}),
      ...scenario,
      opening: scenario.opening ?? null,
      closing: scenario.closing ?? null,
    }))

    return {
      ...(rest as Omit<DispatchMetadata, 'settings' | 'scenarios'>),
      scenarios: reducedScenarios,
      settings: reducedSettings,
    }
  }

  const exportConfiguration = async (
    item: Configurator,
  ): Promise<ReducedDispatchMetadata | false> => {
    if (!item.id || isExportingId.value) return false

    isExportingId.value = item.id

    try {
      const configuration = await getConfigurationData(item.id)
      if (!configuration) {
        throw new Error('missing_configuration')
      }

      const fileName = buildVaExportFileName(item, configuration)
      const reducedConfiguration = reduceConfigurationForExport(configuration)
      exportToJson(reducedConfiguration, undefined, { fileName })
      return reducedConfiguration
    } catch (error) {
      console.error('Failed to export configuration', error)
      toast.error(t('please_try_again'), { title: t('error_occurred') })
      return false
    } finally {
      isExportingId.value = null
    }
  }

  return {
    exportConfiguration,
    isExportingId,
  }
}
